import agwaSectionStyles from './agwa-section.styles.css?inline';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { SlDetails } from '@shoelace-style/shoelace';
import '@shoelace-style/shoelace/dist/components/details/details.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/icon-button/icon-button.js';

/**
 * <agwa-section>
 * Branded collapsible (or static) section wrapper with orange header + action pill slot.
 * Slots:
 *  - (default): body content
 *  - title: custom title content (else headline attr used)
 *  - actions: right aligned actions (wrapped in pill if present)
 *  - prefix: optional leading icon/avatar
 */
@customElement('agwa-section')
export class AgwaSection extends LitElement {
  /** Simple headline text (ignored if a title slot is provided) */
  @property({ type: String }) headline: string = '';
  /** When true the section is open (applies only if collapsible). */
  @property({ type: Boolean, reflect: true }) open = true;
  /** Disable collapse/expand behavior; header rendered without caret */
  @property({ type: Boolean }) collapsible = true;
  /** Dense header/body spacing */
  @property({ type: Boolean }) dense = false;
  /** Visual variant */
  @property({ type: String }) variant: 'brand' | 'neutral' | 'subtle' = 'brand';
  /** Remove inner body padding */
  @property({ type: Boolean, attribute: 'no-body-padding' }) noBodyPadding =
    false;
  /** Show the actions pill area (default true). Set to false to hide entirely. */
  @property({ type: Boolean, attribute: 'show-actions' }) showActions = true;
  static styles = unsafeCSS(agwaSectionStyles);

  private onToggle(e: Event) {
    // sl-details fires 'sl-after-hide' and 'sl-after-show'; we rely on 'sl-show'/'sl-hide' for immediate update
    const target = e.currentTarget as SlDetails;
    if (this.collapsible) {
      this.open = target.open;
      this.dispatchEvent(
        new CustomEvent('agwa-section-toggle', {
          detail: { open: this.open },
          bubbles: true,
          composed: true,
        })
      );
    }
  }

  toggle() {
    if (this.collapsible) this.open = !this.open;
  }
  show() {
    if (this.collapsible) this.open = true;
  }
  hide() {
    if (this.collapsible) this.open = false;
  }

  private renderHeaderInner() {
    const variantClass = this.variant === 'brand' ? '' : this.variant;
    return html`
      <div class="header ${variantClass}" part="header">
        <div class="header-left">
          ${
            this.collapsible
              ? html`<span class="caret" part="caret" aria-hidden="true">
                  <sl-icon name="chevron-right"></sl-icon>
                </span>`
              : html``
          }
          <slot name="prefix"></slot>
          <span class="title" part="title">
            <slot name="title">${this.headline}</slot>
          </span>
        </div>
        ${
          this.showActions
            ? html`
                <div class="header-right" part="actions">
                  <div
                    class="actions-pill"
                    part="actions-pill"
                    data-empty="true"
                    @click=${this.swallowActionsArea}
                    @mousedown=${this.swallowActionsArea}
                    @mouseup=${this.swallowActionsArea}
                    @keydown=${this.onActionsKeyDown}
                  >
                    <slot
                      name="actions"
                      @slotchange=${this.onActionsSlotChange}
                    ></slot>
                  </div>
                </div>
              `
            : html``
        }
      </div>
    `;
  }

  private renderCollapsible() {
    return html`
      <sl-details
        ?open=${this.open}
        @sl-show=${this.onToggle}
        @sl-hide=${this.onToggle}
      >
        <div slot="summary" class="summary-host">
          ${this.renderHeaderInner()}
        </div>
        <div class="body" part="body"><slot></slot></div>
      </sl-details>
    `;
  }

  private renderStatic() {
    const variantClass = this.variant === 'brand' ? '' : this.variant;
    return html`
      <div
        class="frame ${this.variant === 'subtle' ? 'variant-subtle-frame' : ''}"
        part="base"
      >
        <div class=" ${variantClass}" part="header">
          ${this.renderHeaderInner()}
        </div>
        <div class="body" part="body"><slot></slot></div>
      </div>
    `;
  }

  protected updated(changed: Map<string, unknown>) {
    // reflect open prop to underlying sl-details if external code mutates it
    if (changed.has('open') && this.collapsible) {
      const details = this.renderRoot.querySelector('sl-details');
      if (details && details.open !== this.open) details.open = this.open;
    }
    // Re-evaluate actions slot if other props changed that might affect slotted content
    this.updateActionsVisibility();
  }

  private onActionsSlotChange() {
    this.updateActionsVisibility();
  }
  private updateActionsVisibility() {
    const pill = this.renderRoot.querySelector(
      '.actions-pill'
    ) as HTMLElement | null;
    if (!pill) return;
    const slot = pill.querySelector(
      'slot[name="actions"]'
    ) as HTMLSlotElement | null;
    if (!slot) return;
    const assigned = slot.assignedNodes({ flatten: true }).filter((n) => {
      if (n.nodeType === Node.TEXT_NODE)
        return (n.textContent || '').trim().length > 0;
      return true;
    });
    if (assigned.length === 0) pill.setAttribute('data-empty', 'true');
    else pill.setAttribute('data-empty', 'false');
  }

  private swallowActionsArea(e: Event) {
    // Prevent header toggle when clicking inside actions pill
    e.stopPropagation();
    // Prevent default so summary (sl-details) doesn't treat it as toggle activation
    e.preventDefault();
  }
  private onActionsKeyDown(e: KeyboardEvent) {
    if (e.key === ' ' || e.key === 'Enter') {
      // Space/Enter should not toggle the section when focus is inside actions pill
      this.swallowActionsArea(e);
    }
  }

  render() {
    return this.collapsible ? this.renderCollapsible() : this.renderStatic();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-section': AgwaSection;
  }
}
